// services/chatApiService.js - Servicio SEGURO que llama al backend
import apiClient from './api';
import { toast } from 'react-toastify';
import { buildEndpointUrl, getChatMessages } from '../config/chatConfig';

class ChatApiService {
  
  constructor() {
    // Obtener configuración de mensajes
    this.errorMessages = getChatMessages('errorMessages');
    this.successMessages = getChatMessages('successMessages');
    this.infoMessages = getChatMessages('infoMessages');
  }

  // ✅ Enviar mensaje al chat (a través del backend)
  async sendMessage(userMessage, options = {}) {
    try {
      const { provider, model, includeHistory = true } = options;
      
      const requestBody = {
        message: userMessage,
        includeHistory
      };
      
      // Agregar proveedor y modelo si están especificados
      if (provider) requestBody.provider = provider;
      if (model) requestBody.model = model;
      
      // Obtener timeout específico para chat desde configuración
      const chatTimeout = getChatMessages('behavior')?.requestTimeout || 180000; // 3 minutos por defecto
      
      const response = await apiClient.post(buildEndpointUrl('message'), requestBody, {
        timeout: chatTimeout
      });

      // Log para diagnóstico (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Chat API Response:', response.data);
        console.log('📊 Response structure:', {
          hasData: !!response.data,
          hasSuccess: response.data.hasOwnProperty('success'),
          hasMessage: response.data.hasOwnProperty('message'),
          structure: Object.keys(response.data || {})
        });
      }

      // El backend puede devolver la respuesta directamente o envuelta en 'data'
      const responseData = response.data.data || response.data;

      // Verificar que tenemos una respuesta válida con estructura correcta
      if (!responseData || typeof responseData !== 'object') {
        console.error('❌ Invalid response structure: not an object', responseData);
        throw new Error('Respuesta inválida del servidor - formato incorrecto');
      }

      // Verificar success field
      if (typeof responseData.success === 'undefined') {
        console.error('❌ Invalid response structure: missing success field', responseData);
        throw new Error('Respuesta inválida del servidor - falta campo success');
      }

      // Si success es false, manejar como error
      if (responseData.success === false) {
        const errorMessage = responseData.message || responseData.error || 'Error desconocido del servidor';
        console.error('❌ Server returned error:', errorMessage);
        throw new Error(errorMessage);
      }

      // Para respuestas exitosas, verificar que tenemos un mensaje
      if (!responseData.message && typeof responseData.message !== 'string') {
        console.error('❌ Invalid response structure: missing or invalid message field', responseData);
        
        // Intentar proporcionar un mensaje informativo basado en el contexto
        let fallbackMessage = 'Lo siento, hubo un problema con la respuesta del servidor.';
        
        // Si detectamos que es un problema de configuración
        if (responseData.provider === 'no-api-key' || responseData.model === 'configuration-needed') {
          fallbackMessage = 'El chat está disponible pero requiere configuración adicional. Contacta al administrador para activar las respuestas inteligentes.';
        } else if (responseData.provider === 'fallback-mode' || responseData.model === 'error-recovery') {
          fallbackMessage = 'El servicio de chat está en modo limitado. Algunas funciones pueden no estar disponibles temporalmente.';
        } else if (responseData.isTestMode) {
          fallbackMessage = 'El chat está funcionando en modo de prueba. Las respuestas inteligentes pueden estar limitadas.';
        }
        
        return {
          success: true,
          message: fallbackMessage,
          usage: responseData.usage || { tokens: 0 },
          provider: responseData.provider || 'unknown',
          model: responseData.model || 'unknown',
          isTestMode: responseData.isTestMode || true
        };
      }

      return {
        success: true,
        message: responseData.message,
        usage: responseData.usage || { tokens: 0 },
        provider: responseData.provider,
        model: responseData.model,
        isTestMode: responseData.isTestMode
      };

    } catch (error) {
      console.error('❌ Error sending message:', error);
      
      let errorMessage = this.getErrorMessage(error, 'analysisError');

      return {
        success: false,
        error: true,
        message: errorMessage
      };
    }
  }

  // ✅ Generar insight de datos
  async generateInsight(dataType, data, context = '') {
    try {
      const chatTimeout = getChatMessages('behavior')?.requestTimeout || 180000;
      
      const response = await apiClient.post(buildEndpointUrl('insight'), {
        dataType,
        data,
        context
      }, {
        timeout: chatTimeout
      });

      const payload = response.data?.data ?? response.data;
      const insightText = payload?.insight ?? payload?.message;

      if (!insightText) {
        return {
          success: false,
          error: true,
          message: 'El servidor no devolvió un análisis. Verifica la configuración de IA.',
        };
      }

      return {
        success: true,
        message: insightText,
        usage: payload?.usage,
      };

    } catch (error) {
      console.error('Error generating insight:', error);
      
      let errorMessage = this.getErrorMessage(error, 'insightError');
      
      return {
        success: false,
        error: true,
        message: errorMessage
      };
    }
  }

  // ✅ Obtener sugerencias de preguntas (a través del backend)
  async suggestQuestions() {
    try {
      const response = await apiClient.get(buildEndpointUrl('suggestions'));
      return response.data.suggestions || [];

    } catch (error) {
      console.error('Error getting suggestions:', error);
      return [];
    }
  }

  // ✅ Comparar datos (a través del backend)
  async compareData(dataA, dataB, context) {
    try {
      const chatTimeout = getChatMessages('behavior')?.requestTimeout || 180000;
      
      const response = await apiClient.post(buildEndpointUrl('compare'), {
        dataA,
        dataB,
        context
      }, {
        timeout: chatTimeout
      });

      return {
        success: true,
        message: response.data.comparison,
        usage: response.data.usage
      };

    } catch (error) {
      console.error('Error comparing data:', error);
      
      let errorMessage = this.getErrorMessage(error, 'compareError');

      return {
        success: false,
        error: true,
        message: errorMessage
      };
    }
  }

  // ✅ Obtener recomendaciones de optimización (a través del backend)
  async getOptimizationRecommendations(metrics, objective = null) {
    try {
      // Usar objetivo desde configuración si no se especifica
      const defaultObjective = getChatMessages('analysis').defaultObjective;
      const finalObjective = objective || defaultObjective;

      const chatTimeout = getChatMessages('behavior')?.requestTimeout || 180000;

      const response = await apiClient.post(buildEndpointUrl('optimize'), {
        metrics,
        objective: finalObjective
      }, {
        timeout: chatTimeout
      });

      return {
        success: true,
        message: response.data.recommendations,
        usage: response.data.usage
      };

    } catch (error) {
      console.error('Error getting recommendations:', error);
      
      let errorMessage = this.getErrorMessage(error, 'recommendationError');
      
      return {
        success: false,
        error: true,
        message: errorMessage
      };
    }
  }

  // ✅ Analizar tendencias (usando configuración externa)
  async analyzeTrends(timeSeriesData, metric = null) {
    try {
      // Usar métrica desde configuración si no se especifica
      const defaultMetric = getChatMessages('analysis').defaultMetric;
      const finalMetric = metric || defaultMetric;
      
      const prompt = `Analiza las tendencias en estos datos temporales para la métrica ${finalMetric}`;
      
      return await this.generateInsight('general', {
        timeSeriesData,
        metric: finalMetric,
        analysis_type: 'trends'
      }, prompt);

    } catch (error) {
      console.error('Error analyzing trends:', error);
      return {
        success: false,
        error: true,
        message: this.errorMessages.analysisError
      };
    }
  }

  // ✅ Manejo de errores centralizado con configuración externa
  getErrorMessage(error, fallbackKey = 'generic') {
    if (error.response) {
      // Error del servidor con códigos específicos
      switch (error.response.status) {
        case 400:
          return this.errorMessages.badRequest;
        case 401:
          return this.errorMessages.authentication;
        case 403:
          return this.errorMessages.authorization;
        case 429:
          return this.errorMessages.rateLimit;
        case 500:
          return this.errorMessages.serverError;
        default:
          return error.response.data?.error || this.errorMessages[fallbackKey];
      }
    } else if (error.request) {
      // Error de conexión
      return this.errorMessages.network;
    }

    // Error desconocido
    return this.errorMessages.unknown;
  }

  // ✅ Mostrar error con configuración externa
  handleError(error, defaultMessage = null) {
    console.error('ChatAPI Service Error:', error);
    
    const message = defaultMessage || this.getErrorMessage(error);
    
    toast.error(message, {
      theme: 'colored'
    });
    
    return { error: true, message };
  }

  // ✅ Limpiar historial (solo frontend)
  clearHistory() {
    // En esta versión, el historial se maneja en el frontend
    // El backend es stateless por seguridad
    console.log('Historial limpiado');
  }

  // ✅ Obtener historial (solo frontend)
  getHistory() {
    // El backend no mantiene historial por seguridad
    // Cada conversación es independiente
    return [];
  }

  // ✅ Verificar estado del servicio
  async checkHealth() {
    try {
      const response = await apiClient.get(buildEndpointUrl('health'));
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      return { healthy: false, error: this.getErrorMessage(error) };
    }
  }
}

const chatApiService = new ChatApiService();
export default chatApiService; 